import { useState, useEffect } from 'react';
import { Type } from '../const';
import { useNotificationProvider } from 'hooks';
import convertFromTabToGroupSubtab from '../libs/convertFromTabToGroupSubtab';
import countCheckedRow from '../libs/countCheckedRow';
import handleAllChecked from '../libs/handleAllChecked';
import handleRow from '../libs/handleRow';
import makeCheckedTabs from '../libs/makeCheckedTabs';
import updateSubTabApi from 'api/menus/updateSubTabApi';
import { makePostSubtabs } from '../libs/makePostValues';

/**
 * @function useSubTabs
 * @param { Object } props
 * @param { boolean } props.open
 * @param { string } props.groupId
 * @param { string } props.subtabId
 * @param { array } props.tabs
 * @param { array } props.subtabs
 * @param { function } props.onMenu
 * @param { object } props.savingState
 * @param { object } props.savingHandler
 * @param { object } props.handleSubtab
 * @returns { object }
 */
function useSubTabs(props) {
	const { onData, onUpdate } = props.savingHandler;

	const initIcons = { add: true, delete: true };

	const { notifySuccess, notifyError } = useNotificationProvider();

	const [groupSubtabs, setGroupSubtabs] = useState(props.subtabs);
	const [filteredSubtabs, setFilteredSubtabs] = useState([]);
	const [chosenOptions, setChosenOptions] = useState([]);
	const [count, setCount] = useState(0);
	const [isDisabled, setIsDisabled] = useState(initIcons);
	const [isCheckAll, setIsCheckAll] = useState(false);
	const [loading, setLoading] = useState(false);

	const onOptions = (values) => {
		setIsDisabled((prev) => ({ ...prev, add: values.length === 0 }));
		setChosenOptions([...values]);
	};

	const onAdd = () => {
		const results = convertFromTabToGroupSubtab(
			chosenOptions,
			props.groupId,
			props.subtabId,
			groupSubtabs.length + 1,
		);
		setGroupSubtabs([...groupSubtabs, ...results]);
		setChosenOptions([]);
		setIsDisabled(initIcons);
		onData(Type.Subtab, Type.Continue, props.groupId, [...groupSubtabs, ...results]);
		onUpdate(Type.Subtab, true);
	};

	const onDelete = () => {
		const _subtabs = groupSubtabs.filter((t) => !t.checked);
		/** @todo To check the empty tabs... */
		onData(Type.Subtab, Type.Continue, props.groupId, [..._subtabs]);
		setGroupSubtabs([..._subtabs]);
		setIsCheckAll(false);
		onUpdate(Type.Subtab, true);
	};

	const onCheckAll = (status) => {
		if (groupSubtabs.length === 0) return;
		setIsCheckAll(status);
		const results = handleAllChecked(groupSubtabs, status);
		setGroupSubtabs([...results]);
	};

	const onCheck = (id) => {
		const results = makeCheckedTabs(groupSubtabs, id);
		setGroupSubtabs([...results]);
	};

	const onHandle = (e, id) => {
		const results = handleRow(groupSubtabs, id, e.target.name, e.target.value);
		setGroupSubtabs([...results]);
		onData(Type.Subtab, Type.Continue, props.groupId, [...results]);
		onUpdate(Type.Subtab, true);
	};

	const onSetSubtabs = (values) => {
		setGroupSubtabs([...values]);
	};

	const onSave = async () => {
		try {
			setLoading(true);
			props.onMenu(Type.Subtab, groupSubtabs, props.groupId, props.subtabId);
			const _subtabs = await makePostSubtabs(groupSubtabs, props.groupId, props.subtabId);
			const res = await updateSubTabApi(_subtabs);
			onUpdate(Type.Subtab, false);
			notifySuccess(res);
		} catch (error) {
			notifyError(error.message);
		} finally {
			setLoading(false);
		}
	};

	const onReset = () => {
		const results = handleAllChecked(props.subtabs, false);
		setGroupSubtabs([...results]);
		setIsDisabled(initIcons);
		onData(Type.Subtab, Type.Reset, props.groupId);
		onUpdate(Type.Subtab, false);
	};

	useEffect(() => {
		onReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.handleSubtab.trgRstSubtab]);

	useEffect(() => {
		setIsDisabled((prev) => ({ ...prev, delete: count === 0 }));
	}, [count]);

	useEffect(() => {
		if (groupSubtabs.length === 0) {
			setFilteredSubtabs([...props.tabs]);
		} else if (groupSubtabs.length > 0) {
			const _count = countCheckedRow(groupSubtabs);
			const _tabs = props.tabs.filter((t) => !groupSubtabs.some((gst) => t.id === gst.id));
			setCount(_count);
			setFilteredSubtabs([..._tabs]);
		}
	}, [groupSubtabs, props.tabs]);

	const state = { groupSubtabs, filteredSubtabs, chosenOptions, isDisabled, isCheckAll, loading };

	const handler = {
		onOptions,
		onAdd,
		onDelete,
		onCheckAll,
		onCheck,
		onHandle,
		onSetSubtabs,
		onSave,
		onReset,
	};

	return { state, handler };
}

export default useSubTabs;
